import {
  hasUploadFailed,
  isStoryAbandoned,
  isStoryThumbnailsReady,
  isStoryTranscriptionReady,
} from '@src/components/Dashboard/utils';
import { useStorySpeakerIdentificationFactory } from '@src/hooks/useSpeakerIdentificationService';
import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import { resolveTranscriptSpeakers } from '@src/services/SpeakerManagementService';
import { fetchStoryTranscriptElements } from '@src/services/speakers/SpeakerIdentificationService';
import {
  useDashboard,
  useDatoClient,
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { requestMissingFields } from '@src/utility/story';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { comparer, reaction } from 'mobx';
import { useEffect, useRef } from 'react';

import type { DashboardStory } from '@src/types.ts/story';

const logger = LogManager.getLogger('hook:useSpeakerIdentificationPrefetch');

/**
 * Runs speaker identification in the background for every story this session
 * uploads, so the confirmation modal (SpeakerIdentificationPrompt) opens
 * straight onto the suggestions when the upload finishes:
 *
 * - transcript ready → load the transcript and run the transcript pass;
 * - thumbnails extracted (the last pipeline step) → run the frame passes, only
 *   when the transcript pass left a speaker unnamed or partly named.
 *
 * Progress lives in `UserStoryUploadFlowStore.speakerPrefetchRecord`, which is
 * also the dedupe key — this hook remounts with every route, a run in flight
 * does not. Session memory only.
 */
export function useSpeakerIdentificationPrefetch() {
  const dashboardStore = useDashboard();
  const uploadFlow = useUserStoryUploadFlow();
  const datoClient = useDatoClient();
  const speakerService = useSpeakerManagementService();
  const createIdentification = useStorySpeakerIdentificationFactory();
  const userIdentity = useUserIdentity();
  const { enableSpeakersIdentification, enableTranslationReviewerMode } =
    useFlagsCombination(userIdentity.currentRole);
  const enabled =
    enableSpeakersIdentification && !enableTranslationReviewerMode;

  // Read lazily by the reaction so it isn't torn down on every render.
  const depsRef = useRef({ datoClient, speakerService, createIdentification });
  depsRef.current = { datoClient, speakerService, createIdentification };

  useEffect(() => {
    if (!enabled) return;

    const runTranscriptStage = async (story: DashboardStory) => {
      const storyId = story.id;
      uploadFlow.setSpeakerPrefetch(storyId, { status: 'transcript' });
      // Queue the prompt now, not at upload-ready: it opens as soon as there
      // is a result, which can be during Versioning.
      uploadFlow.queueSpeakerPrompt(storyId);
      const { datoClient, speakerService, createIdentification } =
        depsRef.current;

      const transcript = await fetchStoryTranscriptElements(
        {
          getTranscriptionFromDato: id =>
            datoClient.transcriptRepository.getTranscriptionFromDato(id),
          requestTranscription: async id =>
            (await requestMissingFields(id, ['transcription'])).transcription,
          fetchTranscriptFromUrl: url =>
            datoClient.transcriptRepository.fetchTranscriptFromUrl(url),
        },
        story,
      );
      if (!transcript.ok) {
        logger.warn('Background speaker identification: no transcript', {
          storyId,
          error: transcript.error.message,
        });
        uploadFlow.setSpeakerPrefetch(storyId, { status: 'failed' });
        return;
      }
      const elements = transcript.value;

      const speakers = await speakerService.fetchSpeakersForStory(storyId);
      const { anonymous } = resolveTranscriptSpeakers({
        elementLists: [elements],
        mappings: speakers.ok ? speakers.value.mappings : {},
      });
      if (anonymous.length === 0) {
        uploadFlow.setSpeakerPrefetch(storyId, {
          status: 'nothing-to-identify',
        });
        return;
      }

      const identification = createIdentification(
        { id: storyId, title: story.title },
        elements,
      );
      if (!identification) {
        uploadFlow.setSpeakerPrefetch(storyId, { status: 'failed' });
        return;
      }
      const input = { storyId, elements, anonymousSpeakers: anonymous };
      const fromTranscript = await identification.runTranscriptPass(input);
      if (!fromTranscript.ok) {
        uploadFlow.setSpeakerPrefetch(storyId, { status: 'failed' });
        return;
      }
      logger.info('Background speaker identification: transcript pass done', {
        storyId,
        identified: fromTranscript.value.identified.length,
        unidentified: fromTranscript.value.unidentifiedLabels.length,
      });
      uploadFlow.setSpeakerPrefetch(
        storyId,
        // Everyone fully named: the frame passes have nothing to add.
        fromTranscript.value.unidentifiedLabels.length === 0
          ? { status: 'done', result: fromTranscript.value }
          : {
              status: 'awaiting-frames',
              elements,
              anonymousSpeakers: anonymous,
              result: fromTranscript.value,
            },
      );
    };

    const runFrameStage = async (story: DashboardStory) => {
      const storyId = story.id;
      const prefetch = uploadFlow.speakerPrefetchRecord[storyId];
      if (prefetch?.status !== 'awaiting-frames') return;
      uploadFlow.setSpeakerPrefetch(storyId, { ...prefetch, status: 'frames' });

      const identification = depsRef.current.createIdentification(
        { id: storyId, title: story.title },
        prefetch.elements,
      );
      const result = identification
        ? await identification.runFramePasses(
            {
              storyId,
              elements: prefetch.elements,
              anonymousSpeakers: prefetch.anonymousSpeakers,
            },
            prefetch.result,
          )
        : null;
      logger.info('Background speaker identification: frame passes done', {
        storyId,
        ok: !!result?.ok,
      });
      // A failed frame stage still leaves the transcript pass's suggestions.
      uploadFlow.setSpeakerPrefetch(storyId, {
        status: 'done',
        result: result?.ok ? result.value : prefetch.result,
      });
    };

    const start = (run: () => Promise<void>, storyId: string) => {
      run().catch((error: unknown) => {
        logger.error(
          'Background speaker identification failed',
          extractErrorInfo(error),
        );
        uploadFlow.setSpeakerPrefetch(storyId, { status: 'failed' });
      });
    };

    return reaction(
      () => {
        const inPipeline = dashboardStore.allStories.filter(
          s =>
            uploadFlow.getStoryUploadPhase(s.id).phase !== 'idle' &&
            !hasUploadFailed(s) &&
            !isStoryAbandoned(s),
        );
        const inPipelineIds = new Set(inPipeline.map(s => s.id));
        return {
          stories: inPipeline.map(s => ({
            id: s.id,
            transcriptReady: isStoryTranscriptionReady(s),
            thumbnailsReady: isStoryThumbnailsReady(s),
            status: uploadFlow.speakerPrefetchRecord[s.id]?.status,
          })),
          // Left the pipeline (failed, abandoned, phase cleared) before its
          // thumbnails — whichever came first, the frame passes never will.
          stranded: Object.entries(uploadFlow.speakerPrefetchRecord)
            .filter(
              ([id, prefetch]) =>
                prefetch.status === 'awaiting-frames' && !inPipelineIds.has(id),
            )
            .map(([id]) => id),
        };
      },
      ({ stories, stranded }) => {
        // Settle on the transcript pass's suggestions, so the prompt opens on
        // them instead of waiting on frame passes that cannot start.
        for (const id of stranded) {
          const prefetch = uploadFlow.speakerPrefetchRecord[id];
          if (prefetch?.status !== 'awaiting-frames') continue;
          uploadFlow.setSpeakerPrefetch(id, {
            status: 'done',
            result: prefetch.result,
          });
        }
        for (const s of stories) {
          const story = dashboardStore.allStories.find(x => x.id === s.id);
          if (!story) continue;
          if (!s.status && s.transcriptReady) {
            start(() => runTranscriptStage(story), s.id);
          } else if (s.status === 'awaiting-frames' && s.thumbnailsReady) {
            start(() => runFrameStage(story), s.id);
          }
        }
      },
      { fireImmediately: true, equals: comparer.structural },
    );
  }, [enabled, dashboardStore, uploadFlow]);
}
